import React, { useMemo, useCallback, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Panel,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';

const CRITICALITY_COLORS = {
  critical: '#ef4444',
  premium: '#f59e0b',
  standard: '#22c55e',
};

const OperationNode = ({ data }) => {
  const bgColor = data.isSource
    ? '#3b82f6'
    : (data.teamColor || data.criticalityColor || CRITICALITY_COLORS[data.criticality] || '#64748b');

  const hasFlows = data.flows && data.flows.length > 0;

  return (
    <div
      style={{
        background: bgColor,
        border: data.isSource ? '3px solid #1e40af' : '2px solid rgba(255,255,255,0.3)',
        borderRadius: '10px',
        padding: '14px 18px',
        color: 'white',
        minWidth: '140px',
        maxWidth: '180px',
        textAlign: 'center',
        boxShadow: data.isSource
          ? '0 4px 12px rgba(59, 130, 246, 0.4)'
          : '0 2px 8px rgba(0,0,0,0.15)',
        cursor: 'pointer',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        position: 'relative',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.05)';
        e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.25)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = data.isSource
          ? '0 4px 12px rgba(59, 130, 246, 0.4)'
          : '0 2px 8px rgba(0,0,0,0.15)';
      }}
    >
      {/* Connection handles for edges */}
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: '#94a3b8', width: 8, height: 8 }}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: '#94a3b8', width: 8, height: 8 }}
      />

      <div style={{ fontWeight: 600, fontSize: '13px', lineHeight: 1.3 }}>{data.label}</div>
      {data.team && (
        <div style={{ fontSize: '10px', opacity: 0.9, marginTop: '4px', fontWeight: 500 }}>
          {data.team}
        </div>
      )}
      {hasFlows && (
        <div style={{
          marginTop: '6px',
          paddingTop: '6px',
          borderTop: '1px solid rgba(255,255,255,0.3)',
          fontSize: '10px',
          opacity: 0.9
        }}>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px'
          }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 3v2H5v2h4v2H7v2h2v2H7v2h2v2H5v2h4v2h2V3H9zm6 0v6h4V7h-4V5h4V3h-4zm0 8v2h4v-2h-4zm0 4v2h4v-2h-4zm0 4v2h4v-2h-4z"/>
            </svg>
            {data.flowCount} flow{data.flowCount !== 1 ? 's' : ''}
          </span>
        </div>
      )}
    </div>
  );
};

const nodeTypes = { operation: OperationNode };

const getLayoutedElements = (nodes, edges) => {
  if (!nodes || nodes.length === 0) {
    return { nodes: [], edges: edges || [] };
  }

  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: 'LR', nodesep: 80, ranksep: 150, marginx: 40, marginy: 40 });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: 180, height: 80 });
  });

  (edges || []).forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    if (!nodeWithPosition) {
      return { ...node, position: { x: 0, y: 0 } };
    }
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - 90,
        y: nodeWithPosition.y - 40,
      },
    };
  });

  return { nodes: layoutedNodes, edges: edges || [] };
};

export default function BlastRadiusCanvas({ diagram, operationId }) {
  const [selectedNode, setSelectedNode] = useState(null);

  // Mark the source operation node with isSource flag for highlighting
  const processedDiagram = useMemo(() => {
    if (!diagram || !operationId) {
      return diagram;
    }
    return {
      ...diagram,
      nodes: (diagram.nodes || []).map(node => ({
        ...node,
        data: {
          ...node.data,
          isSource: String(node.data?.id) === String(operationId) || node.data?.isSource,
        },
      })),
    };
  }, [diagram, operationId]);

  const { nodes: layoutedNodes, edges: layoutedEdges } = useMemo(
    () => getLayoutedElements(processedDiagram?.nodes || [], processedDiagram?.edges || []),
    [processedDiagram]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(layoutedNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(layoutedEdges);

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node.data);
  }, []);

  if (!diagram?.nodes?.length) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#64748b',
        flexDirection: 'column',
        gap: '8px',
      }}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="12" cy="12" r="10"/>
          <path d="M12 8v4M12 16h.01"/>
        </svg>
        <div>No downstream dependencies to visualize.</div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.3 }}
        attributionPosition="bottom-left"
        proOptions={{ hideAttribution: true }}
        defaultEdgeOptions={{
          type: 'smoothstep',
        }}
      >
        <Background color="#e2e8f0" gap={20} size={1} />
        <Controls showInteractive={false} />

        {/* Legend Panel */}
        <Panel position="top-left" style={{ background: 'white', padding: '12px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', fontSize: '11px' }}>
          <div style={{ fontWeight: 600, marginBottom: '8px', color: '#1e293b' }}>Dependency Types</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '24px', height: '2px', background: '#ef4444' }}></div>
              <span style={{ color: '#64748b' }}>Runtime (animated)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '24px', height: '2px', background: '#3b82f6' }}></div>
              <span style={{ color: '#64748b' }}>Data</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '24px', height: '2px', background: '#10b981' }}></div>
              <span style={{ color: '#64748b' }}>Event</span>
            </div>
          </div>
        </Panel>

        {/* Selected Node Info Panel */}
        {selectedNode && (
          <Panel position="top-right" style={{
            background: 'white',
            padding: '16px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            fontSize: '12px',
            maxWidth: '260px',
            minWidth: '200px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b' }}>{selectedNode.label}</div>
                {selectedNode.team && (
                  <div style={{ color: '#64748b', marginTop: '2px' }}>{selectedNode.team}</div>
                )}
              </div>
              <button
                onClick={() => setSelectedNode(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#94a3b8',
                  padding: '2px',
                  fontSize: '16px',
                  lineHeight: 1
                }}
              >
                ×
              </button>
            </div>

            <div style={{
              display: 'inline-block',
              padding: '3px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: 500,
              background: `${selectedNode.criticalityColor}20`,
              color: selectedNode.criticalityColor,
              marginBottom: '12px'
            }}>
              {selectedNode.criticality}
            </div>

            {selectedNode.flows && selectedNode.flows.length > 0 && (
              <div>
                <div style={{ fontWeight: 600, color: '#475569', marginBottom: '8px', borderTop: '1px solid #e2e8f0', paddingTop: '12px' }}>
                  Linked Flows ({selectedNode.flows.length})
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {selectedNode.flows.map((flow, idx) => (
                    <a
                      key={idx}
                      href={`/flows/${encodeURIComponent(flow.slug)}`}
                      style={{
                        color: '#6366f1',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                      onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 3v2H5v2h4v2H7v2h2v2H7v2h2v2H5v2h4v2h2V3H9z"/>
                      </svg>
                      {flow.name}
                    </a>
                  ))}
                </div>
              </div>
            )}

            <a
              href={`/operations/${encodeURIComponent(selectedNode.slug)}`}
              style={{
                display: 'block',
                marginTop: '12px',
                padding: '8px 12px',
                background: '#f1f5f9',
                borderRadius: '6px',
                color: '#475569',
                textDecoration: 'none',
                textAlign: 'center',
                fontWeight: 500,
                fontSize: '11px'
              }}
              onMouseEnter={(e) => e.target.style.background = '#e2e8f0'}
              onMouseLeave={(e) => e.target.style.background = '#f1f5f9'}
            >
              View Operation Details →
            </a>
          </Panel>
        )}
      </ReactFlow>
    </div>
  );
}
